<template>
  <div class="modal fade" id="vpnModal" tabindex="-1" aria-hidden="true" ref="modalEl">
    <div class="modal-dialog modal-dialog-centered w-[460px]">
      <div class="modal-content" v-if="props.license">
        <div class="modal-header">
          <h1 class="modal-title fs-5">VPN for {{ props.license.host_domain }}:{{ props.license.http_port }}</h1>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body px-4">
          <div v-if="loading" class="text-gray-500 py-3">Loading…</div>
          <template v-else-if="vpn && vpn.supported">
            <!-- ProProxyUI: WireGuard and OpenVPN are always ready, one file each -->
            <!-- ProxySmart: the OpenVPN profile (download link and its QR code) -->
            <div v-if="vpn.ps" class="space-y-3">
              <div class="space-y-1" style="border: 1px solid var(--ui-line); border-radius: 8px; padding: 8px 10px;">
                <div class="flex items-center gap-2"><img :src="openVpnLogo" alt="" style="width: 22px; height: 22px;"/><b>OpenVPN</b></div>
                <div class="flex gap-3 items-center pt-1">
                  <img v-if="psQrUrl" :src="psQrUrl" alt="OpenVPN QR code" style="width: 140px; height: 140px; flex: none; background: #ffffff; border-radius: 6px; padding: 4px;"/>
                  <div class="space-y-1">
                    <div class="text-sm"><b>On a phone:</b> scan with the camera. The profile downloads: open it with OpenVPN Connect.</div>
                    <div class="text-sm text-gray-500">On a computer: download the file and open it with OpenVPN.</div>
                    <div class="pt-1"><button class="btn btn-primary btn-sm" :disabled="busy" @click="downloadPs">Download .ovpn file</button></div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="vpn.ppui" class="space-y-3">
              <div v-if="vpn.wg" class="space-y-1" style="border: 1px solid var(--ui-line); border-radius: 8px; padding: 8px 10px;">
                <div class="flex items-center justify-between"><span class="flex items-center gap-2"><img :src="wireGuardLogo" alt="" style="width: 22px; height: 22px; background: #ffffff; border-radius: 50%; padding: 2px;"/><b>WireGuard</b></span><span class="font-mono text-sm">{{ vpn.host }}:{{ vpn.wg.port }}</span></div>
                <div v-if="vpn.live" class="text-sm" :class="vpn.live.wg_connected ? 'text-green-500' : 'text-gray-500'">
                  <v-icon icon="mdi-circle" size="9" class="me-1" :color="vpn.live.wg_connected ? '#22c55e' : '#ef4444'"></v-icon>{{ vpn.live.wg_connected ? 'Connected' : 'Not connected' }}<span v-if="vpn.live.wg_seen" class="text-gray-500"> · last seen {{ ago(vpn.live.wg_seen) }}</span>
                </div>
                <div class="flex gap-3 items-center pt-1">
                  <img v-if="wgQrUrl" :src="wgQrUrl" alt="WireGuard QR code" style="width: 140px; height: 140px; flex: none; background: #ffffff; border-radius: 6px; padding: 4px;"/>
                  <div class="space-y-1">
                    <div class="text-sm"><b>On a phone:</b> open the WireGuard app, tap +, then "Scan from QR code".</div>
                    <div class="text-sm text-gray-500">On a computer: download the file. No login needed, the keys are inside it.</div>
                    <div class="flex gap-2 pt-1">
                      <button class="btn btn-primary btn-sm" :disabled="busy" @click="download('wg')">Download .conf file</button>
                      <button class="btn btn-outline-secondary btn-sm" :disabled="busy" @click="newKeys">New keys</button>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="vpn.ovpn" class="space-y-1" style="border: 1px solid var(--ui-line); border-radius: 8px; padding: 8px 10px;">
                <div class="flex items-center justify-between"><span class="flex items-center gap-2"><img :src="openVpnLogo" alt="" style="width: 22px; height: 22px;"/><b>OpenVPN</b></span><span class="font-mono text-sm">{{ vpn.host }}:{{ vpn.ovpn.port }} {{ vpn.ovpn.proto }}</span></div>
                <div v-if="vpn.live" class="text-sm" :class="vpn.live.ovpn_connected ? 'text-green-500' : 'text-gray-500'">
                  <v-icon icon="mdi-circle" size="9" class="me-1" :color="vpn.live.ovpn_connected ? '#22c55e' : '#ef4444'"></v-icon>{{ vpn.live.ovpn_connected ? 'Connected' : 'Not connected' }}<span class="text-gray-500"> · {{ vpn.live.ovpn_sessions === 1 ? '1 open session' : vpn.live.ovpn_sessions + ' open sessions' }}</span>
                </div>
                <div v-if="!vpn.ovpn_login" class="text-sm text-gray-500">OpenVPN logs in with a username and password. Set a username:password login under Authentication to use it.</div>
                <div v-else class="flex gap-3 items-center pt-1">
                  <img v-if="ovpnQrUrl" :src="ovpnQrUrl" alt="OpenVPN QR code" style="width: 140px; height: 140px; flex: none; background: #ffffff; border-radius: 6px; padding: 4px;"/>
                  <div class="space-y-1">
                    <div class="text-sm"><b>On a phone:</b> scan with the camera. The profile downloads: open it with OpenVPN Connect.</div>
                    <div class="text-sm text-gray-500">The QR link works for 10 minutes. Login: your proxy username and password (<span class="font-mono">{{ vpn.user }}</span>).</div>
                    <div class="pt-1"><button class="btn btn-primary btn-sm" :disabled="busy" @click="download('ovpn')">Download .ovpn file</button></div>
                  </div>
                </div>
              </div>
              <div class="text-sm text-gray-500">Data today: {{ vpn.used_mb }} MB<span v-if="vpn.quota_gb"> of {{ vpn.quota_gb }} GB</span>, shared by the proxy and the VPN.</div>
              <div class="text-xs text-gray-500">New keys, or a new proxy password, make a new WireGuard file: download it again afterwards.</div>
            </div>
            <!-- enabled -->
            <div v-else-if="vpn.enabled" class="space-y-2">
              <div class="flex items-center justify-between">
                <span>{{ vpn.type_label }} is <b class="text-green-600">enabled</b></span>
                <span v-if="vpn.blocked" class="text-red-500 text-sm">paused: daily allowance used</span>
              </div>
              <div class="text-sm">Server <span class="font-mono">{{ vpn.host }}:{{ vpn.port }}</span></div>
              <div v-if="vpn.type === 'opvn'" class="text-sm">Login: your proxy username and password (<span class="font-mono">{{ vpn.user }}</span>)</div>
              <div v-else class="text-sm">No login needed: the keys are inside the config file.</div>
              <div class="text-sm text-gray-500">
                Data today: proxy {{ vpn.proxy_used_mb }} MB + VPN {{ vpn.vpn_used_mb }} MB
                <span v-if="vpn.quota_mb"> of {{ vpn.quota_mb }} MB shared allowance ({{ vpn.remaining_mb }} MB left)</span>
                <span v-else> (no daily limit)</span>
              </div>
              <div class="flex gap-2 pt-2">
                <button class="btn btn-primary btn-sm" :disabled="busy" @click="download">
                  Download {{ vpn.type === 'wg' ? '.conf' : '.ovpn' }} file
                </button>
                <button class="btn btn-outline-danger btn-sm" :disabled="busy" @click="disable">Disable VPN</button>
              </div>
              <div class="text-xs text-gray-500 pt-1">Disabling and re-enabling issues new keys, so previously downloaded files stop working. Data used through the VPN counts against the same daily allowance as the proxy.</div>
            </div>
            <!-- not enabled -->
            <div v-else class="space-y-3">
              <div v-if="vpn.server_off" class="text-sm" style="color: var(--ui-warn-ink); background: var(--ui-warn-soft); border: 1px solid var(--ui-warn); border-radius: 8px; padding: 8px 10px;">
                The VPN is switched off on the server at the moment. Turn it on again below, then download a fresh file.
              </div>
              <div class="text-sm">Turn on a VPN that uses this proxy's mobile connection. Pick one type; it can be changed later.</div>
              <v-radio-group v-model="pickedType" inline hide-details density="compact">
                <v-radio v-for="t in vpn.types" :key="t.value" :label="t.name" :value="t.value"></v-radio>
              </v-radio-group>
              <div class="text-xs text-gray-500">
                WireGuard: fastest, keys in the file, no login. OpenVPN: logs in with your proxy username and password.
                VPN data counts against the same daily allowance as the proxy<span v-if="vpn.quota_mb"> ({{ vpn.quota_mb }} MB)</span>.
              </div>
              <button class="btn btn-primary btn-sm" :disabled="busy || !pickedType" @click="enable">Enable {{ pickedType === 'wg' ? 'WireGuard' : pickedType === 'opvn' ? 'OpenVPN' : '' }}</button>
            </div>
          </template>
          <div v-else class="text-gray-500 py-3">VPN is not available for this proxy.</div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, defineProps, defineEmits } from 'vue';
import { useProxyStore, useAlertStore } from "@/stores"
import { wgQr, ovpnQr, linkQr } from '@/helpers/vpnQr';
import openVpnLogo from '@/assets/openVPN.png';
import wireGuardLogo from '@/assets/wireguard.svg';

const props = defineProps({ license: { type: Object, required: true } });
const emit = defineEmits(['close']);
const proxyStore = useProxyStore();
const alertStore = useAlertStore();

const vpn = ref(null);
const loading = ref(false);
const busy = ref(false);
const pickedType = ref('wg');

async function load() {
  if (!props.license) { vpn.value = null; return; }
  if (props.license.m_type === 'PROXYSMART') {   // ProxySmart: OpenVPN only, from the proxy's download link
    vpn.value = { supported: !!props.license.openvpn_link, ps: true };
    psQrUrl.value = props.license.openvpn_link ? await linkQr(props.license.openvpn_link) : null;
    return;
  }
  loading.value = true;
  vpn.value = await proxyStore.getVpn(props.license.uuid);
  wgQrUrl.value = null; ovpnQrUrl.value = null; ovpnQrAt = 0;
  if (vpn.value?.ppui) { makeWgQr(); makeOvpnQr(); }
  if (vpn.value?.server_off && vpn.value.type) pickedType.value = vpn.value.type;   // offer the same type again
  loading.value = false;
}
// Load only while this dialog is open (the licence prop is shared with every dialog on the page).
const modalEl = ref(null);
const isOpen = () => !!modalEl.value && modalEl.value.classList.contains('show');
watch(() => props.license, () => { if (isOpen()) load(); });
// while the window stays open the state is re-read every 10 minutes (quietly)
let refreshTimer = null;
const refresh = async () => { if (!isOpen() || busy.value || !props.license || props.license.m_type === 'PROXYSMART') return; const next = await proxyStore.getVpnQuiet(props.license.uuid); if (next) vpn.value = next; };
// ProProxyUI: the connected state is live, so it is re-read every 30 s while the window is open
let liveTimer = null;
const refreshLive = () => { if (vpn.value?.ppui) { refresh(); if (ovpnQrAt && Date.now() - ovpnQrAt > 9 * 60 * 1000) makeOvpnQr(); } };
// ProProxyUI QR codes: WireGuard = the profile; OpenVPN = a 10-minute download link (made again before it runs out)
const wgQrUrl = ref(null);
const ovpnQrUrl = ref(null);
let ovpnQrAt = 0;
const makeWgQr = async () => { wgQrUrl.value = null; if (vpn.value?.ppui && vpn.value.wg) wgQrUrl.value = await wgQr(props.license.uuid); };
const makeOvpnQr = async () => { ovpnQrUrl.value = null; ovpnQrAt = 0; if (vpn.value?.ppui && vpn.value.ovpn && vpn.value.ovpn_login) { ovpnQrUrl.value = await ovpnQr(props.license.uuid); ovpnQrAt = Date.now(); } };
onMounted(() => { modalEl.value && modalEl.value.addEventListener('shown.bs.modal', load); refreshTimer = setInterval(refresh, 10 * 60 * 1000); liveTimer = setInterval(refreshLive, 30000); });
onBeforeUnmount(() => { modalEl.value && modalEl.value.removeEventListener('shown.bs.modal', load); clearInterval(refreshTimer); clearInterval(liveTimer); });
// "18 s ago", "4 min ago", "2 h ago"
const ago = (iso) => {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (!Number.isFinite(s)) return '';
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

async function enable() {
  busy.value = true;
  const res = await proxyStore.enableVpn(props.license.uuid, pickedType.value);
  if (res) { vpn.value = res; alertStore.success("VPN enabled. Download the file to connect."); emit('close'); }
  busy.value = false;
}
async function disable() {
  if (!window.confirm("Disable the VPN? Your downloaded file will stop working.")) return;
  busy.value = true;
  const res = await proxyStore.disableVpn(props.license.uuid);
  if (res) { vpn.value = res; alertStore.success("VPN disabled"); emit('close'); }
  busy.value = false;
}
// ProxySmart OpenVPN
const psQrUrl = ref(null);
async function downloadPs() {
  busy.value = true;
  try {
    const r = await fetch(props.license.openvpn_link);
    if (!r.ok) throw new Error((await r.json().catch(() => null))?.message || 'The OpenVPN file could not be downloaded');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(await r.blob());
    a.download = `proxy-${props.license.http_port}.ovpn`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  } catch (e) { alertStore.error(e.message || e); }
  busy.value = false;
}
async function newKeys() {
  if (!window.confirm("Make new WireGuard keys? Your downloaded .conf file will stop working.")) return;
  busy.value = true;
  const res = await proxyStore.enableVpn(props.license.uuid, 'wg-new-keys');
  if (res) { vpn.value = res; alertStore.success("New keys made. Scan the new QR code or download the new .conf file."); makeWgQr(); }
  busy.value = false;
}
async function download(type) {
  busy.value = true;
  const f = await proxyStore.vpnFile(props.license.uuid, typeof type === 'string' ? type : undefined);   // the old button passes the click event
  if (f && f.content) {
    const blob = new Blob([f.content], { type: 'application/octet-stream' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = f.filename; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  }
  busy.value = false;
}
</script>
